import { useEffect, useState } from "react";
import { Navigate, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import axios from "axios";
import { toast } from "sonner";
import {
  SignOut,
  Calendar,
  Users,
  Receipt,
  Envelope,
  CurrencyEur,
  Clock,
  CheckCircle,
  XCircle,
  Phone,
  MapPin,
  Download,
  CaretDown,
} from "@phosphor-icons/react";
import { useAuth } from "../context/AuthContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const TOKEN_KEY = "pixora_admin_token";

const FORMULA_LABELS = {
  start: "Start",
  signature: "Signature",
  prestige: "Prestige",
  wedding: "Wedding",
  "pro-digital": "Pro Digital",
  "pro-print": "Pro Print",
};

const STATUS_RES_LABELS = {
  pending: { text: "En attente", cls: "bg-amber-900/30 text-amber-300 border-amber-700/40" },
  confirmed: { text: "Confirmée", cls: "bg-emerald-900/30 text-emerald-300 border-emerald-700/40" },
  cancelled: { text: "Annulée", cls: "bg-red-900/30 text-red-300 border-red-700/40" },
};
const STATUS_QUOTE_LABELS = {
  pending: { text: "En attente", cls: "bg-amber-900/30 text-amber-300 border-amber-700/40" },
  accepted: { text: "Accepté", cls: "bg-emerald-900/30 text-emerald-300 border-emerald-700/40" },
  rejected: { text: "Refusé", cls: "bg-red-900/30 text-red-300 border-red-700/40" },
};

function fmtDate(iso) {
  if (!iso) return "—";
  const d = typeof iso === "string" ? new Date(iso) : iso;
  if (isNaN(d.getTime())) return iso;
  return d.toLocaleDateString("fr-FR", { day: "2-digit", month: "short", year: "numeric" });
}

function fmtDateTime(iso) {
  if (!iso) return "—";
  const d = typeof iso === "string" ? new Date(iso) : iso;
  if (isNaN(d.getTime())) return iso;
  return d.toLocaleString("fr-FR", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" });
}

function StatCard({ icon: Icon, label, value, suffix, tone = "gold" }) {
  const tones = {
    gold: "from-[#C9A962]/20 to-transparent border-[#C9A962]/30 text-[#C9A962]",
    green: "from-emerald-500/15 to-transparent border-emerald-700/40 text-emerald-300",
    amber: "from-amber-500/15 to-transparent border-amber-700/40 text-amber-300",
  };
  return (
    <div className={`rounded-2xl bg-gradient-to-br ${tones[tone]} border p-5`}>
      <div className="flex items-center justify-between mb-2">
        <span className="text-[11px] uppercase tracking-wider text-[#B8A88A]">{label}</span>
        <Icon size={20} weight="fill" className={tones[tone].split(" ").pop()} />
      </div>
      <div className="text-3xl font-extrabold text-white">
        {value}
        {suffix && <span className="text-lg text-[#B8A88A] ml-1">{suffix}</span>}
      </div>
    </div>
  );
}

function Tab({ id, active, onClick, icon: Icon, label, count }) {
  const isActive = active === id;
  return (
    <button
      type="button"
      onClick={() => onClick(id)}
      data-testid={`admin-tab-${id}`}
      className={`flex items-center gap-2 px-4 py-2.5 rounded-full text-sm font-semibold transition-all ${
        isActive
          ? "bg-gradient-to-r from-[#D4BA7A] to-[#C9A962] text-black shadow-lg"
          : "bg-black/40 border border-[#C9A962]/30 text-[#B8A88A] hover:border-[#C9A962]/60"
      }`}
    >
      <Icon size={16} weight={isActive ? "fill" : "regular"} />
      {label}
      {count !== undefined && (
        <span className={`ml-1 px-2 py-0.5 rounded-full text-[11px] ${isActive ? "bg-black/20" : "bg-[#C9A962]/15"}`}>
          {count}
        </span>
      )}
    </button>
  );
}

function Pill({ label, cls, testid }) {
  return <span data-testid={testid} className={`px-2.5 py-0.5 rounded-full border text-[11px] font-semibold ${cls}`}>{label}</span>;
}

function StatusMenu({ current, options, onChange, testid }) {
  const [open, setOpen] = useState(false);
  const label = options.find((o) => o.value === current)?.text || current;
  return (
    <div className="relative" data-testid={testid}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-[#1a1a1a] border border-[#C9A962]/30 text-[#C9A962] text-xs font-medium hover:border-[#C9A962] transition-colors"
      >
        {label}
        <CaretDown size={12} weight="bold" />
      </button>
      {open && (
        <>
          <div className="fixed inset-0 z-10" onClick={() => setOpen(false)} />
          <div className="absolute right-0 top-full mt-1 z-20 min-w-[150px] rounded-xl bg-[#0A0A0A] border border-[#C9A962]/40 shadow-xl overflow-hidden">
            {options.map((o) => (
              <button
                key={o.value}
                type="button"
                onClick={() => {
                  setOpen(false);
                  if (o.value !== current) onChange(o.value);
                }}
                className={`block w-full text-left px-4 py-2 text-sm transition-colors ${
                  o.value === current ? "bg-[#C9A962]/15 text-[#F5E6B8]" : "text-[#B8A88A] hover:bg-[#C9A962]/10 hover:text-white"
                }`}
              >
                {o.text}
              </button>
            ))}
          </div>
        </>
      )}
    </div>
  );
}

export default function AdminDashboard() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [tab, setTab] = useState("reservations");
  const [stats, setStats] = useState(null);
  const [reservations, setReservations] = useState([]);
  const [proQuotes, setProQuotes] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState("all");

  const token = () => localStorage.getItem(TOKEN_KEY);
  const headers = () => ({ Authorization: `Bearer ${token()}` });

  useEffect(() => {
    if (user === null) return;
    if (!user) { navigate("/admin", { replace: true }); return; }
    (async () => {
      try {
        const [s, r, q, c] = await Promise.all([
          axios.get(`${API}/admin/stats`, { headers: headers() }),
          axios.get(`${API}/admin/reservations`, { headers: headers() }),
          axios.get(`${API}/admin/pro-quotes`, { headers: headers() }),
          axios.get(`${API}/admin/contacts`, { headers: headers() }),
        ]);
        setStats(s.data);
        setReservations(r.data);
        setProQuotes(q.data);
        setContacts(c.data);
      } catch (err) {
        if (err?.response?.status === 401) logout();
        else toast.error("Impossible de charger les données.");
      } finally {
        setLoading(false);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  if (user === null) return <div className="min-h-screen bg-[#0b0b0b] flex items-center justify-center text-[#C9A962]">Chargement…</div>;
  if (!user) return <Navigate to="/admin" replace />;

  const updateReservation = async (id, status) => {
    try {
      const { data } = await axios.patch(`${API}/admin/reservations/${id}`, { status }, { headers: headers() });
      setReservations((prev) => prev.map((r) => (r.id === id ? data : r)));
      toast.success(`Réservation ${STATUS_RES_LABELS[status]?.text.toLowerCase()}.`);
    } catch {
      toast.error("Mise à jour impossible.");
    }
  };

  const updateQuote = async (id, status) => {
    try {
      const { data } = await axios.patch(`${API}/admin/pro-quotes/${id}`, { status }, { headers: headers() });
      setProQuotes((prev) => prev.map((q) => (q.id === id ? data : q)));
      toast.success(`Devis ${STATUS_QUOTE_LABELS[status]?.text.toLowerCase()}.`);
    } catch {
      toast.error("Mise à jour impossible.");
    }
  };

  const filteredReservations = reservations.filter((r) =>
    statusFilter === "all" ? true : (r.status || "pending") === statusFilter
  );

  return (
    <div className="min-h-screen bokeh-strong pb-16" data-testid="admin-dashboard">
      {/* Topbar */}
      <div className="sticky top-0 z-30 backdrop-blur-md bg-[#0b0b0b]/85 border-b border-[#C9A962]/20">
        <div className="max-w-7xl mx-auto px-4 md:px-8 py-4 flex items-center justify-between">
          <div>
            <p className="text-[10px] tracking-[0.3em] text-[#C9A962]">ADMIN</p>
            <h1 className="text-xl md:text-2xl font-extrabold text-white leading-tight">
              Tableau de bord <span className="gradient-text">Pixora</span>
            </h1>
          </div>
          <div className="flex items-center gap-3">
            <span className="hidden sm:inline text-xs text-[#8A7A5A]">{user.email}</span>
            <button
              type="button"
              onClick={() => { logout(); navigate("/admin", { replace: true }); }}
              data-testid="admin-logout"
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-full border border-[#C9A962]/40 text-[#C9A962] text-xs font-semibold hover:bg-[#C9A962]/10"
            >
              <SignOut size={14} weight="bold" />
              Déconnexion
            </button>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 md:px-8 pt-6">
        {/* Stats */}
        {stats && (
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            className="grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-5 mb-8"
            data-testid="admin-stats"
          >
            <StatCard icon={Calendar} label="Réservations" value={stats.reservations_total} />
            <StatCard icon={Clock} label="En attente" value={stats.reservations_pending} tone="amber" />
            <StatCard icon={CheckCircle} label="À venir" value={stats.reservations_upcoming} tone="green" />
            <StatCard icon={CurrencyEur} label="CA estimé" value={stats.estimated_revenue.toLocaleString("fr-FR")} suffix="€" />
          </motion.div>
        )}

        {/* Tabs */}
        <div className="flex flex-wrap gap-2 md:gap-3 mb-6">
          <Tab id="reservations" active={tab} onClick={setTab} icon={Calendar} label="Réservations" count={reservations.length} />
          <Tab id="pro-quotes" active={tab} onClick={setTab} icon={Receipt} label="Devis Pro" count={proQuotes.length} />
          <Tab id="contacts" active={tab} onClick={setTab} icon={Envelope} label="Contacts" count={contacts.length} />
        </div>

        {loading ? (
          <div className="text-center text-[#8A7A5A] py-20">Chargement…</div>
        ) : (
          <>
            {tab === "reservations" && (
              <div data-testid="admin-section-reservations">
                <div className="flex items-center gap-2 mb-4 overflow-x-auto">
                  {[
                    { v: "all", t: `Toutes (${reservations.length})` },
                    { v: "pending", t: `En attente (${reservations.filter((r) => (r.status || "pending") === "pending").length})` },
                    { v: "confirmed", t: `Confirmées (${reservations.filter((r) => r.status === "confirmed").length})` },
                    { v: "cancelled", t: `Annulées (${reservations.filter((r) => r.status === "cancelled").length})` },
                  ].map((f) => (
                    <button
                      key={f.v}
                      type="button"
                      onClick={() => setStatusFilter(f.v)}
                      className={`whitespace-nowrap px-3 py-1.5 rounded-full text-xs font-medium border transition ${
                        statusFilter === f.v
                          ? "bg-[#C9A962]/20 border-[#C9A962] text-[#F5E6B8]"
                          : "bg-black/30 border-[#C9A962]/20 text-[#B8A88A] hover:border-[#C9A962]/50"
                      }`}
                    >
                      {f.t}
                    </button>
                  ))}
                </div>

                <div className="space-y-3">
                  {filteredReservations.length === 0 && (
                    <p className="text-center text-[#8A7A5A] py-10">Aucune réservation.</p>
                  )}
                  {filteredReservations.map((r) => {
                    const st = STATUS_RES_LABELS[r.status || "pending"];
                    return (
                      <motion.div
                        key={r.id}
                        initial={{ opacity: 0, y: 6 }}
                        animate={{ opacity: 1, y: 0 }}
                        className="rounded-xl bg-[#0A0A0A] border border-[#C9A962]/20 p-5 hover:border-[#C9A962]/50 transition-colors"
                        data-testid={`admin-reservation-${r.id}`}
                      >
                        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                          <div className="flex-1 min-w-0">
                            <div className="flex flex-wrap items-center gap-2 mb-2">
                              <h3 className="text-lg font-bold text-white truncate">{r.name}</h3>
                              <Pill label={st.text} cls={st.cls} testid={`admin-res-status-${r.id}`} />
                              <span className="px-2 py-0.5 rounded-full bg-[#C9A962]/10 border border-[#C9A962]/30 text-[#C9A962] text-[11px] font-semibold">
                                {FORMULA_LABELS[r.formula] || r.formula}
                              </span>
                            </div>
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-1 text-sm text-[#B8A88A]">
                              <div className="flex items-center gap-2"><Calendar size={14} className="text-[#C9A962]" /><strong className="text-white">{fmtDate(r.event_date)}</strong></div>
                              <div className="flex items-center gap-2"><Envelope size={14} className="text-[#C9A962]" /><a href={`mailto:${r.email}`} className="hover:text-[#C9A962] truncate">{r.email}</a></div>
                              <div className="flex items-center gap-2"><Phone size={14} className="text-[#C9A962]" /><a href={`tel:${r.phone}`} className="hover:text-[#C9A962]">{r.phone}</a></div>
                              <div className="flex items-center gap-2"><MapPin size={14} className="text-[#C9A962]" />{r.distance_km ? `${r.distance_km} km` : "—"} · {r.event_type}</div>
                            </div>
                            {(r.event_address || r.event_city) && (
                              <div className="mt-2 text-xs text-[#D8CCA8] flex items-start gap-2">
                                <MapPin size={14} className="text-[#C9A962] shrink-0 mt-0.5" />
                                <span className="break-words">
                                  {r.event_address}{r.event_address && (r.event_postal_code || r.event_city) ? ", " : ""}{r.event_postal_code} {r.event_city}
                                </span>
                              </div>
                            )}
                            {r.options?.length > 0 && (
                              <div className="mt-2 text-xs text-[#8A7A5A]">Options : {r.options.join(", ")}</div>
                            )}
                            {r.message && (
                              <p className="mt-2 text-sm text-[#D8CCA8] italic bg-black/30 border-l-2 border-[#C9A962] pl-3 py-1">
                                « {r.message} »
                              </p>
                            )}
                          </div>
                          <div className="flex md:flex-col items-start md:items-end gap-2 md:min-w-[160px]">
                            <div className="text-xl font-extrabold gradient-text">
                              {r.total_estimate ? `${r.total_estimate.toFixed(2)} €` : "—"}
                            </div>
                            <StatusMenu
                              current={r.status || "pending"}
                              testid={`admin-res-menu-${r.id}`}
                              onChange={(s) => updateReservation(r.id, s)}
                              options={[
                                { value: "pending", text: "En attente" },
                                { value: "confirmed", text: "Confirmer" },
                                { value: "cancelled", text: "Annuler" },
                              ]}
                            />
                            <span className="text-[10px] text-[#5A5040]">Reçu {fmtDateTime(r.created_at)}</span>
                          </div>
                        </div>
                      </motion.div>
                    );
                  })}
                </div>
              </div>
            )}

            {tab === "pro-quotes" && (
              <div className="space-y-3" data-testid="admin-section-pro-quotes">
                {proQuotes.length === 0 && <p className="text-center text-[#8A7A5A] py-10">Aucun devis pour l'instant.</p>}
                {proQuotes.map((q) => {
                  const st = STATUS_QUOTE_LABELS[q.status || "pending"];
                  return (
                    <div key={q.id} className="rounded-xl bg-[#0A0A0A] border border-[#C9A962]/20 p-5" data-testid={`admin-quote-${q.id}`}>
                      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                        <div className="flex-1 min-w-0">
                          <div className="flex flex-wrap items-center gap-2 mb-2">
                            <h3 className="text-lg font-bold text-white truncate">{q.company}</h3>
                            <Pill label={st.text} cls={st.cls} />
                            <span className="px-2 py-0.5 rounded-full bg-[#C9A962]/10 border border-[#C9A962]/30 text-[#C9A962] text-[11px] font-semibold">
                              {FORMULA_LABELS[q.formula] || q.formula}
                            </span>
                          </div>
                          <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-1 text-sm text-[#B8A88A]">
                            {q.contact_name && <div>👤 {q.contact_name}</div>}
                            <div><a href={`mailto:${q.email}`} className="hover:text-[#C9A962]">{q.email}</a></div>
                            {q.phone && <div><a href={`tel:${q.phone}`} className="hover:text-[#C9A962]">{q.phone}</a></div>}
                            <div>Date : <strong className="text-white">{fmtDate(q.event_date)}</strong>{q.guest_count ? ` · ${q.guest_count} invités` : ""}</div>
                          </div>
                          {q.message && (
                            <p className="mt-2 text-sm text-[#D8CCA8] italic bg-black/30 border-l-2 border-[#C9A962] pl-3 py-1">« {q.message} »</p>
                          )}
                        </div>
                        <div className="flex flex-col items-start md:items-end gap-2 md:min-w-[160px]">
                          <a
                            href={`${API}/pro-quotes/${q.id}/pdf`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-[#C9A962]/15 border border-[#C9A962]/40 text-[#F5E6B8] text-xs font-semibold hover:bg-[#C9A962]/25"
                            data-testid={`admin-quote-pdf-${q.id}`}
                          >
                            <Download size={14} weight="bold" />
                            PDF du devis
                          </a>
                          <StatusMenu
                            current={q.status || "pending"}
                            onChange={(s) => updateQuote(q.id, s)}
                            options={[
                              { value: "pending", text: "En attente" },
                              { value: "accepted", text: "Accepter" },
                              { value: "rejected", text: "Refuser" },
                            ]}
                          />
                          <span className="text-[10px] text-[#5A5040]">Reçu {fmtDateTime(q.created_at)}</span>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}

            {tab === "contacts" && (
              <div className="space-y-3" data-testid="admin-section-contacts">
                {contacts.length === 0 && <p className="text-center text-[#8A7A5A] py-10">Aucun message de contact.</p>}
                {contacts.map((c) => (
                  <div key={c.id} className="rounded-xl bg-[#0A0A0A] border border-[#C9A962]/20 p-5" data-testid={`admin-contact-${c.id}`}>
                    <div className="flex flex-wrap items-center gap-2 mb-2">
                      <h3 className="text-lg font-bold text-white">{c.name}</h3>
                      <span className="text-xs text-[#5A5040]">{fmtDateTime(c.created_at)}</span>
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-1 text-sm text-[#B8A88A] mb-2">
                      <div><a href={`mailto:${c.email}`} className="hover:text-[#C9A962]">{c.email}</a></div>
                      {c.phone && <div><a href={`tel:${c.phone}`} className="hover:text-[#C9A962]">{c.phone}</a></div>}
                      {c.event_type && <div>Type : {c.event_type}</div>}
                      {c.event_date && <div>Date : {fmtDate(c.event_date)}</div>}
                    </div>
                    {c.message && (
                      <p className="text-sm text-[#D8CCA8] italic bg-black/30 border-l-2 border-[#C9A962] pl-3 py-1">« {c.message} »</p>
                    )}
                  </div>
                ))}
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
